import React, { useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  MultiFactorResolver,
  TotpMultiFactorGenerator,
  PhoneMultiFactorGenerator,
} from 'firebase/auth';

interface MFAVerificationProps {
  open: boolean;
  resolver: MultiFactorResolver | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const MFAVerification: React.FC<MFAVerificationProps> = ({ open, resolver, onSuccess, onCancel }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const MAX_ATTEMPTS = 3;

  const handleSubmit = async () => {
    if (!resolver) return;
    if (!code.trim()) {
      setError('Please enter the verification code');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const totpHint = resolver.hints.find(
        (h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID
      );
      if (!totpHint) {
        throw new Error('No TOTP factor enrolled. Phone-based MFA is not implemented in this build.');
      }
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, code.trim());
      await resolver.resolveSignIn(assertion);
      onSuccess();
    } catch (err: any) {
      const next = attempts + 1;
      setAttempts(next);
      if (next >= MAX_ATTEMPTS) {
        setError('Too many failed attempts. Please try logging in again.');
        setTimeout(onCancel, 2500);
      } else {
        setError(err?.message || `Invalid code. ${MAX_ATTEMPTS - next} attempts remaining.`);
      }
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Two-Factor Authentication</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="body1" sx={{ mb: 3 }}>
          Enter the 6-digit code from your authenticator app.
        </Typography>
        <TextField
          label="Verification Code"
          variant="outlined"
          fullWidth
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={handleKeyDown}
          inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
          disabled={loading}
          sx={{ mb: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || code.length !== 6 || attempts >= MAX_ATTEMPTS || !resolver}
        >
          {loading ? <CircularProgress size={24} /> : 'Verify'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Phone MFA isn't enabled in this build but the import keeps the symbol available.
void PhoneMultiFactorGenerator;

export default MFAVerification;
